import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import {
  DestinoGrupo,
  GrupoSolicitudesDTO,
  OrganizacionDTO,
  SedeDTO,
} from '../../clientes/utilidades/clientes.models';
import { ListaDTO, Parametros } from '../../compartido/utilidades/compartido.models';
import { ErrorFrontend } from '../utilidades/error.utils';

@Injectable({ providedIn: 'root', deps: [HttpClient] })
export class OrganizacionService {
  /* Organizaciones */
  private _organizaciones: OrganizacionDTO[] | undefined;
  private _organizacionesPromise$: Promise<OrganizacionDTO[]> | undefined;
  public get organizaciones$(): Promise<OrganizacionDTO[]> {
    if (this._organizacionesPromise$) return this._organizacionesPromise$;
    if (this._organizaciones) return Promise.resolve(this._organizaciones);
    this._organizacionesPromise$ = this._obtenerOrganizaciones$().then(
      (organizaciones: OrganizacionDTO[]) => {
        this._organizaciones = organizaciones;
        this._organizacionesPromise$ = undefined;
        return organizaciones;
      },
    );
    return this._organizacionesPromise$;
  }

  constructor(private readonly http: HttpClient) {}

  /**
   * Obtiene la lista de organizaciones del servidor.
   * @returns {Promise<OrganizacionDTO[]>} - La lista de organizaciones como llega del servidor (promise).
   * @author Juan Corral
   */
  private async _obtenerOrganizaciones$(): Promise<OrganizacionDTO[]> {
    return firstValueFrom(
      this.http.get<ListaDTO<OrganizacionDTO>>('organizaciones/').pipe(
        map((res: ListaDTO<OrganizacionDTO>) => res.results),
        catchError(() => of([])),
      ),
    );
  }

  /**
   * Obtiene una organización por su ID.
   * @param {number} id - El ID de la organización a obtener.
   * @returns {Promise<OrganizacionDTO>} - La organización encontrada (promise).
   * @throws {ErrorFrontend} - Si no se encuentra la organización.
   * @author Juan Corral
   */
  public async obtenerOrganizacion$(id: number): Promise<OrganizacionDTO> {
    const organizaciones = await this.organizaciones$;
    const organizacion = organizaciones.find((org) => org.id === id);
    if (!organizacion) throw new ErrorFrontend('No se encontró la organización');
    return Promise.resolve(organizacion);
  }

  /**
   * Obtiene las sedes de una organización.
   * @param {OrganizacionDTO} organizacion - La organización de la que se desean obtener las sedes.
   * @returns {Promise<SedeDTO[]>} - La lista de sedes de la organización como llega del servidor (promise).
   * @author Juan Corral
   */
  public async obtenerSedes$(organizacion: OrganizacionDTO): Promise<SedeDTO[]> {
    const params: Parametros = { organizacion: organizacion.id };
    return firstValueFrom(
      this.http.get<ListaDTO<SedeDTO>>('organizaciones/sedes/', { params }).pipe(
        map((res: ListaDTO<SedeDTO>) => res.results),
        catchError(() => of([])),
      ),
    );
  }

  /**
   * Obtiene un grupo de solicitudes con sus organizaciones activas para el destino de su
   * landing, cada una con su cobertura.
   * @param {string} slug - El slug del grupo (el de la URL de la landing).
   * @param {DestinoGrupo} destino - La página a la que lleva la landing.
   * @returns {Promise<GrupoSolicitudesDTO>} - El grupo como llega del servidor (promise).
   * @throws {ErrorFrontend} - Si el grupo no existe (status 404) o hubo un error.
   * @author Juan Corral
   */
  public obtenerGrupo$(slug: string, destino: DestinoGrupo): Promise<GrupoSolicitudesDTO> {
    const params: Parametros = { destino };
    return firstValueFrom(
      this.http.get<GrupoSolicitudesDTO>(`organizaciones/grupos/${slug}/`, { params }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Limpia los datos almacenados en el servicio.
   * @author Juan Corral
   */
  public limpiar(): void {
    this._organizaciones = undefined;
    this._organizacionesPromise$ = undefined;
  }
}
